import { useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button, Badge } from 'react-bootstrap';
import { DataTable } from '../components/DataTable';
import { TablePageLayout } from '../components/TablePageLayout';
import { StatusBadge } from '../components/StatusBadge';
import { fetchDashboardDeliveries } from '../api/dashboardApi';
import { useQuery } from '@tanstack/react-query';
import { formatDate } from '../utils/formatters';
import { useScrollRestoration } from '../hooks/useScrollRestoration';

const stateColors = {
  draft: 'secondary',
  waiting: 'info',
  confirmed: 'info',
  assigned: 'info',
  done: 'success',
};

const stateLabels = {
  draft: { emoji: '📝', label: 'Draft' },
  waiting: { emoji: '⏳', label: 'Waiting' },
  confirmed: { emoji: '⏳', label: 'Waiting' },
  assigned: { emoji: '⏳', label: 'Waiting' },
  done: { emoji: '✅', label: 'Done' },
};

const serialsStatusLabels = {
  'open': 'Not requested',
  'not_available': 'Not available',
  'in_progress': 'Processing',
  'done': 'Received',
};

const docStateLabels = {
  False: 'Missing',
  no: 'Not needed',
  open: 'Needed',
  in_progress: 'In progress',
  done: 'Received',
};

const typeColors = {
  outgoing: 'danger',
  incoming: 'success',
  internal: 'info',
};

const typeLabels = {
  outgoing: { emoji: '🚚', label: 'Outgoing' },
  incoming: { emoji: '📥', label: 'Incoming' },
  internal: { emoji: '🔄', label: 'Internal' },
};


export function DeliveryListPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialType = searchParams.get('type');
  const defaultFilter = ['outgoing', 'incoming', 'internal'].includes(initialType?.toLowerCase())
    ? initialType.charAt(0).toUpperCase() + initialType.slice(1).toLowerCase()
    : 'All';

  const [filter, setFilter] = useState(defaultFilter);

  const handleFilterChange = (label) => {
    setFilter(label);
    if (label === 'All') {
      setSearchParams({});
    } else {
      setSearchParams({ type: label.toLowerCase() });
    }
  };

  const { data, isLoading, isError, error, isFetched } = useQuery({
    queryKey: ['dashboard-deliveries'],
    queryFn: fetchDashboardDeliveries,
  });

  const { containerRef, handleScroll } = useScrollRestoration(
    `deliveryListScroll_${filter}`,
    isFetched
  );

  const rows = useMemo(() => {
    if (!data || !data.transfers) return [];

    return data.transfers
      .filter((transfer) => {
        if (filter === 'All') return true;
        return transfer.picking_type_code?.toLowerCase() === filter.toLowerCase();
      })
      .map((transfer) => {
        const typeCode = transfer.picking_type_code?.toLowerCase();
        let serialColor = 'success';
        if (
          (transfer.serial_numbers_state === 'open' && transfer.state === 'done') ||
          (transfer.serial_numbers_state === 'in_progress' && (transfer.state === 'assigned' || transfer.state === 'done'))
        ) {
          serialColor = 'danger';
        } else if (transfer.serial_numbers_state === 'open') {
          serialColor = 'info';
        }

        let docColor = 'success';
        if (
            transfer.state === 'done' && (transfer.customs_documents_state === 'open' || transfer.customs_documents_state === 'in_progress') ||
            transfer.customs_documents_state === 'False'
        ) {
          docColor = 'danger';
        }

        return [
          <StatusBadge value={typeCode} colors={typeColors} labels={typeLabels} />,
          transfer.responsible_person_name || '-',
          transfer.sales_order?.user_id?.name || transfer.purchase_order?.user_id?.name || '-',
          <a href={transfer.url} target="_blank" rel="noreferrer">{transfer.name}</a>,
          transfer.origin,
          transfer.partner?.url ? (
            <a href={transfer.partner.url} target="_blank" rel="noreferrer">{transfer.partner.name}</a>
          ) : (
            transfer.partner?.name || '-'
          ),
          <StatusBadge value={transfer.state} colors={stateColors} labels={stateLabels} />,
          formatDate(transfer.scheduled_date),
          <Badge bg={docColor} title={docStateLabels[transfer.customs_documents_state === 'False' ? 'False' : transfer.customs_documents_state]?.split(' ')[1] || ''}>
            {docStateLabels[transfer.customs_documents_state === 'False' ? 'False' : transfer.customs_documents_state] || '-'}
          </Badge>,
          <Badge bg={serialColor} title={serialsStatusLabels[transfer.serial_numbers_state]?.split(' ')[1] || ''}>
            {serialsStatusLabels[transfer.serial_numbers_state] || '-'}
          </Badge>,
        ];
      });
  }, [data, filter]);

  return (
    <TablePageLayout
      title="Deliveries with missing docs"
      description="Transfer list with outgoing, incoming and internal filters."
      action={
        <div className="d-flex gap-2 flex-wrap">
          {['All', 'Outgoing', 'Incoming', 'Internal'].map((label) => (
            <Button
              key={label}
              variant={filter === label ? 'warning' : 'primary'}
              onClick={() => handleFilterChange(label)}
            >
              {label}
            </Button>
          ))}
        </div>
      }
      isLoading={isLoading}
      isError={isError}
      error={error}
      containerRef={containerRef}
      onScroll={handleScroll}
    >
      <DataTable
        headers={['Type', 'Responsible (most likely)', 'Manager (order)', 'Transfer', 'Order', 'Partner', 'State', 'Scheduled', 'Customs docs', 'Serials']}
        rows={rows}
        className="sticky-header"
      />
    </TablePageLayout>
  );
}
